/**
 * Chat Service - Handles chat messages and API calls
 */

import { OpenAIService } from "./openaiService.js";
import { preparePromptData, createResponseFormat } from "./promptService.js";
import { 
  estimateTokens, 
  validateAndAdjustData, 
  highlightChangedCells, 
  handleApiError,
  appendMessage,
  showThinkingIndicator,
  hideThinkingIndicator
} from "../utils/helpers.js";
import { getCurrentUserEmail } from "./authService.js";
import { getCurrentQuota, updateQuotaDisplay, getRagFeatures } from "./quotaService.js";
import { isEditMode, getCurrentModel } from "../ui/settingsUI.js";
import { 
  buildSharePointContext, 
  isSharePointConfigured, 
  loadSharePointConfig 
} from "./sharePointService.js";
import {
  buildAzureSearchContext,
  isAzureSearchAvailable
} from "./ragService.js";
import { isExcelHost } from "../config/hostConfig.js";
import { EPJ_TOOLS, buildEpjAgentPrompt, createEpjToolExecutor, previewEpjEdits, applyEpjEdits, downloadEpjFile, getEpjOverview } from "./epjEditService.js";
import { EPLAN_TOOLS, triggerEplanAction, buildEplanCommand } from "./eplanService.js";

let openaiService = null;
let useRAGContext = true; // RAG context is enabled by default
let conversationHistory = []; // Session-local chat memory
const MAX_HISTORY_MESSAGES = 20; // Keep last N message pairs to limit token usage

// ── EPJ mode ──────────────────────────────────────────────────────────────────
// When active, main-chat messages are routed to the EPJ agent instead of
// the normal chat. History is kept separately to include tool messages.
let epjMode = null; // { fileName, toolExecutor, history }

export function activateEpjMode(fileName, overview, file = null) {
  const systemPrompt = buildEpjAgentPrompt(fileName);
  const toolExecutor = createEpjToolExecutor(overview);
  epjMode = {
    fileName,
    toolExecutor,
    history: [{ role: 'system', content: systemPrompt }],
    file   // original File object for re-upload if backend loses the file
  };
}

export function deactivateEpjMode() {
  epjMode = null;
}

export function isEpjModeActive() {
  return !!epjMode;
}

export function getEpjFileName() {
  return epjMode?.fileName ?? null;
}

export function getOpenAIService() {
  return openaiService;
}

export function setUseRAGContext(enabled) {
  useRAGContext = enabled;
}

export function getUseRAGContext() {
  return useRAGContext;
}

export function initializeOpenAIService(apiKey, userEmail) {
  openaiService = new OpenAIService(apiKey, userEmail);
  return openaiService;
}

export function clearOpenAIService() {
  openaiService = null;
  conversationHistory = [];
}

export function clearChatHistory() {
  conversationHistory = [];
}

export function getChatHistory() {
  return conversationHistory;
}

export function setOpenAIServiceModel(model) {
  if (openaiService) {
    openaiService.deploymentName = model;
  }
}

// Re-export UI functions for convenience
export { appendMessage, showThinkingIndicator, hideThinkingIndicator };

// Main message handling
export async function sendMessage() {
  const currentUserEmail = getCurrentUserEmail();
  const currentQuota = getCurrentQuota();
  
  // Check if user is logged in
  if (!currentUserEmail) {
    appendMessage("❌ Bitte melde dich zuerst an, um den Assistenten zu nutzen.");
    return;
  }

  // Check quota before making API call
  if (currentQuota) {
    if (currentQuota.daily.tokens_remaining <= 0) {
      const quotaType = currentQuota.type === 'domain' ? 'Gemeinsames Tageslimit' : 'Dein Tageslimit';
      appendMessage(`❌ ${quotaType} von ${currentQuota.daily.tokens_limit.toLocaleString('de-DE')} Tokens ist erreicht. Wird um Mitternacht zurückgesetzt.`);
      return;
    }
    if (currentQuota.monthly.tokens_remaining <= 0) {
      const quotaType = currentQuota.type === 'domain' ? 'Gemeinsames Monatslimit' : 'Dein Monatslimit';
      appendMessage(`❌ ${quotaType} von ${currentQuota.monthly.tokens_limit.toLocaleString('de-DE')} Tokens ist erreicht. Wird nächsten Monat zurückgesetzt.`);
      return;
    }
  }

  if (!openaiService) {
    console.error("OpenAI service not initialized");
    return;
  }

  const chatInput = document.getElementById("chat-input");
  const message = chatInput.value.trim();
  
  if (!message) return;

  // EPJ mode takes priority over Excel — route to EPJ agent regardless of host
  console.log('[sendMessage] epjMode:', epjMode?.fileName ?? null);
  if (epjMode) {
    appendMessage(message, true);
    chatInput.value = '';
    await handleEpjAgentStep(message);
    return;
  }

  if (!isExcelHost()) {
    // Standalone browser mode - chat without Excel data
    await handleStandaloneMessage(message, chatInput);
    return;
  }

  try {
    await Excel.run(async (context) => {
      const selectedRange = context.workbook.getSelectedRange();
      const sheet = selectedRange.worksheet;
      
      // Prepare prompt data
      const promptData = await preparePromptData(sheet, selectedRange, message);
      
      if (!promptData) {
        appendMessage("❌ Keine gültigen Daten ausgewählt.");
        return;
      }
      if (promptData.error) {
        appendMessage(promptData.error);
        return;
      }
      
      await handleDataRequest(sheet, promptData, message, chatInput);
    });
  } catch (error) {
    console.error("Error in sendMessage():", error);
    appendMessage(`❌ Fehler: ${error.message}`);
  }
}

/**
 * Run one user turn of the EPJ agent loop.
 * Keeps going until the model produces a text response (question/info)
 * or calls submit_operations (operations ready → triggers preview in dialog).
 */
async function handleEpjAgentStep(message) {
  epjMode.history.push({ role: 'user', content: message });
  showThinkingIndicator();

  try {
    while (true) {
      const response = await openaiService._rawChat(epjMode.history, { tools: EPJ_TOOLS, tool_choice: 'auto' });
      const msg = response.message;
      epjMode.history.push({ role: 'assistant', content: msg.content || null, tool_calls: msg.tool_calls });

      if (!msg.tool_calls || msg.tool_calls.length === 0) {
        hideThinkingIndicator();
        appendMessage(msg.content || '(Keine Antwort)');
        return;
      }

      for (const tc of msg.tool_calls) {
        let args;
        try { args = JSON.parse(tc.function.arguments); } catch { args = {}; }

        if (tc.function.name === 'submit_operations') {
          if (args?.operations?.length) {
            hideThinkingIndicator();
            // Add tool response BEFORE returning so history stays valid for follow-up messages
            epjMode.history.push({ role: 'tool', tool_call_id: tc.id, content: JSON.stringify({ success: true, operationsQueued: args.operations.length }) });
            handleEpjOperationsReady(args);
            return;
          }
          // Missing operations — tell model to retry
          epjMode.history.push({ role: 'tool', tool_call_id: tc.id, content: JSON.stringify({ error: 'Das "operations"-Array fehlt oder ist leer. Bitte erneut aufrufen.' }) });
          continue;
        }

        const result = await epjMode.toolExecutor(tc.function.name, args);
        epjMode.history.push({ role: 'tool', tool_call_id: tc.id, content: JSON.stringify(result) });
      }
    }
  } catch (error) {
    hideThinkingIndicator();
    appendMessage(`❌ EPJ-Fehler: ${error.message}`);
  }
}

/**
 * Called when the EPJ agent calls submit_operations.
 * Renders a preview in the main chat with Apply / Cancel / Download buttons.
 */
async function handleEpjOperationsReady({ text, operations }) {
  if (!epjMode) return;
  const fileName = epjMode.fileName;

  const messagesDiv = document.getElementById('chat-messages');
  if (!messagesDiv) return;

  // Show AI explanation text first
  if (text) appendMessage(text);

  let preview;
  try {
    preview = await previewEpjEdits(fileName, operations);
  } catch (err) {
    if (err.message.includes('nicht gefunden') && epjMode?.file) {
      try {
        await uploadEpjForEditing(epjMode.file, () => {});
        preview = await previewEpjEdits(fileName, operations);
      } catch (retryErr) {
        appendMessage(`Vorschau fehlgeschlagen: ${retryErr.message}`);
        return;
      }
    } else {
      appendMessage(`Vorschau fehlgeschlagen: ${err.message}`);
      return;
    }
  }

  // Build preview message element
  const wrap = document.createElement('div');
  wrap.className = 'message assistant-message epj-preview-message';

  const opLabels = {
    'set-attribute': 'Attribut setzen',
    'copy-node': 'Knoten kopieren',
    'remove-node': 'Element entfernen',
    'set-text': 'Text setzen',
  };

  let html = '<div class="epj-preview-box">';
  html += '<strong>Vorschau der Anderungen:</strong>';
  html += '<ul class="epj-preview-list">';
  for (const p of preview.previews || []) {
    const icon = p.canApply ? '✓' : '✗';
    const label = opLabels[p.op] || p.op;
    const pathStr = escapeHtml(p.path || p.sourcePath || '');
    let item = `${icon} <strong>${label}</strong> — <code>${pathStr}</code>`;
    if (p.currentValue !== undefined && p.newValue !== undefined) {
      item += `<br><span class="epj-old-value">${escapeHtml(String(p.currentValue || '(leer)'))}</span> → <span class="epj-new-value">${escapeHtml(String(p.newValue))}</span>`;
    }
    if (!p.canApply && p.reason) {
      item += `<br><em>${escapeHtml(p.reason)}</em>`;
    }
    html += `<li>${item}</li>`;
  }
  html += '</ul>';
  if (!preview.canApplyAll) {
    html += '<p class="epj-warning">Einige Operationen konnen nicht angewendet werden.</p>';
  }
  html += `
    <div class="epj-preview-actions">
      <button class="ms-Button ms-Button--primary epj-apply-btn"><span>Anwenden</span></button>
      <button class="ms-Button ms-Button--default epj-cancel-btn"><span>Abbrechen</span></button>
    </div>`;
  html += '</div>';
  wrap.innerHTML = html;

  wrap.querySelector('.epj-cancel-btn').addEventListener('click', () => {
    wrap.querySelector('.epj-preview-actions').outerHTML = '<p><em>Abgebrochen.</em></p>';
  });

  wrap.querySelector('.epj-apply-btn').addEventListener('click', async () => {
    const actionsEl = wrap.querySelector('.epj-preview-actions');
    actionsEl.innerHTML = '<em>Wird angewendet...</em>';
    try {
      let result;
      try {
        result = await applyEpjEdits(fileName, operations);
      } catch (applyErr) {
        if (applyErr.message.includes('nicht gefunden') && epjMode?.file) {
          await uploadEpjForEditing(epjMode.file, () => {});
          result = await applyEpjEdits(fileName, operations);
        } else throw applyErr;
      }
      if (result.success) {
        // Refresh device map so follow-up prompts see newly added/renamed devices
        try {
          const newOverview = await getEpjOverview(fileName);
          if (epjMode) epjMode.toolExecutor = createEpjToolExecutor(newOverview);
        } catch { /* non-fatal — old map stays active */ }

        actionsEl.innerHTML =
          `<p>✓ <strong>${result.applied}</strong> von ${result.total} Operationen angewendet.</p>` +
          `<br><br><button class="ms-Button ms-Button--primary epj-download-btn">` +
          `<i class="ms-Icon ms-Icon--Download"></i><span> Datei herunterladen</span></button>`;
        actionsEl.querySelector('.epj-download-btn').addEventListener('click', async () => {
          try {
            await downloadEpjFile(fileName);
          } catch (e) {
            appendMessage(`Download fehlgeschlagen: ${e.message}`);
          }
        });
      } else {
        const errs = (result.errors || []).map(e => `<li>${escapeHtml(e)}</li>`).join('');
        actionsEl.innerHTML = `<p>Bearbeitung fehlgeschlagen.</p><ul>${errs}</ul>`;
      }
    } catch (err) {
      actionsEl.innerHTML = `<p>Fehler beim Anwenden: ${escapeHtml(err.message)}</p>`;
    }
  });

  messagesDiv.appendChild(wrap);
  messagesDiv.scrollTop = messagesDiv.scrollHeight;
}

function escapeHtml(text) {
  if (!text) return '';
  const d = document.createElement('div');
  d.textContent = String(text);
  return d.innerHTML;
}

async function handleStandaloneMessage(message, chatInput) {
  const currentQuota = getCurrentQuota();
  const currentModel = getCurrentModel();

  const backgroundInfo = (await import("../ui/backgroundDialog.js")).getBackgroundInfo();
  const backgroundContext = (backgroundInfo && backgroundInfo.trim())
    ? `\n\nZusätzliche Hintergrundinformationen vom Benutzer:\n${backgroundInfo}`
    : "";

  const hasEplan = getRagFeatures().includes('eplan');
  const eplanToolHint = hasEplan ? '\nWenn der Benutzer eine EPLAN-Automatisierungsaufgabe anfragt (z.B. Projekt drucken, exportieren oder sichern), nutze das eplan_execute-Tool.' : '';
  let systemPrompt = `Du bist LEA, ein hilfreicher logischer Engineering Assistent der CAE Expert Group.\nDu unterstützt bei Fragen rund um EPLAN, ECAD-Daten und Artikeldaten.\nBeantworte die Fragen des Benutzers so hilfreich wie möglich.${eplanToolHint}${backgroundContext}`;
  const userPrompt = message;

  const estimatedTokensCount = estimateTokens(systemPrompt + userPrompt);

  if (currentQuota && currentQuota.limits && estimatedTokensCount > currentQuota.limits.max_tokens_per_request) {
    appendMessage(`❌ Deine Anfrage mit ca. ${estimatedTokensCount} Tokens überschreitet dein Limit von ${currentQuota.limits.max_tokens_per_request} Tokens pro Anfrage.`);
    return;
  }

  appendMessage(message, true);
  chatInput.value = "";

  const estimatedDiv = document.getElementById("estimated-usage");
  if (estimatedDiv) estimatedDiv.textContent = "";

  showThinkingIndicator();

  try {
    // Build RAG context
    let ragContext = null;
    if (useRAGContext) {
      const features = getRagFeatures();
      const hasCloud = features.includes('cloud');
      const hasUpload = features.includes('upload');
      const contextParts = [];

      if (hasCloud || hasUpload) {
        if (hasCloud && isSharePointConfigured()) {
          try {
            const config = loadSharePointConfig();
            const spContext = await buildSharePointContext(message, config);
            if (spContext) contextParts.push(`=== SharePoint/OneDrive Dokumente ===\n${spContext}`);
          } catch (ragError) {
            console.warn("SharePoint search failed, continuing:", ragError);
          }
        }
        if (hasUpload) {
          try {
            const azureContext = await buildAzureSearchContext(message);
            if (azureContext) contextParts.push(`=== Hochgeladene Dokumente ===\n${azureContext}`);
          } catch (ragError) {
            console.warn("Azure Search failed, continuing:", ragError);
          }
        }
        if (contextParts.length > 0) ragContext = contextParts.join('\n\n');
      }
    }

    if (ragContext) {
      systemPrompt = `${systemPrompt}\n\n--- RELEVANTER KONTEXT AUS DOKUMENTEN ---\n${ragContext}\n--- ENDE KONTEXT ---\nNutze diese Informationen wenn sie für die Anfrage relevant sind.`;
    }

    // Add user message to history
    conversationHistory.push({ role: "user", content: userPrompt });

    // Trim history to limit token usage
    if (conversationHistory.length > MAX_HISTORY_MESSAGES) {
      conversationHistory = conversationHistory.slice(-MAX_HISTORY_MESSAGES);
    }

    const messages = [
      { role: "system", content: systemPrompt },
      ...conversationHistory
    ];
    let response = await openaiService._rawChat(messages, hasEplan
      ? { tools: EPLAN_TOOLS, tool_choice: 'auto' }
      : { responseFormat: { type: 'text' } });

    // Handle eplan_execute tool call if the AI requested one
    if (response.message.tool_calls?.length) {
      const tc = response.message.tool_calls[0];
      let args;
      try { args = JSON.parse(tc.function.arguments); } catch { args = {}; }

      let toolResult;
      if (tc.function.name === 'eplan_execute') {
        // Validate that params.TYPE is present — without it W3u.exe will fail
        if (!args.params || !args.params.TYPE) {
          toolResult = {
            error: `FEHLER: params.TYPE fehlt. Das Feld params ist PFLICHT und muss mindestens TYPE enthalten.\n` +
              `Beispiel für PDF-Export: { "action": "export", "project": "...", "params": { "TYPE": "PDFPROJECTSCHEME", "EXPORTFILE": "C:\\\\Users\\\\...\\\\Datei.pdf" } }\n` +
              `Frag den Benutzer nach dem gewünschten Zieldateipfad (EXPORTFILE), falls nicht angegeben, und wiederhole den Tool-Aufruf dann korrekt.`
          };
        } else if (args.action === 'backup' && (!args.params.ARCHIVENAME || !args.params.DESTINATIONPATH || !args.params.BACKUPMETHOD)) {
          const missing = [];
          if (!args.params.ARCHIVENAME) missing.push('ARCHIVENAME (nur Dateiname ohne Pfad, z.B. "Demo.zw1")');
          if (!args.params.DESTINATIONPATH) missing.push('DESTINATIONPATH (Zielverzeichnis, z.B. "C:\\\\backup")');
          if (!args.params.BACKUPMETHOD) missing.push('BACKUPMETHOD (muss genau BACKUP, SOURCEOUT oder ARCHIVE sein)');
          toolResult = {
            error: `FEHLER: Pflichtparameter für backup fehlen: ${missing.join(', ')}.\n` +
              `WICHTIG: ARCHIVENAME enthält NUR den Dateinamen OHNE Verzeichnis!\n` +
              `Korrektes Beispiel: { "TYPE": "PROJECT", "ARCHIVENAME": "Demo.zw1", "DESTINATIONPATH": "C:\\\\backup", "BACKUPMETHOD": "BACKUP" }\n` +
              `Frag den Benutzer nach den fehlenden Werten und wiederhole den Tool-Aufruf korrekt.`
          };
        } else {
          try {
            const cmdLine = buildEplanCommand(args.action, args.project, args.params);
            appendMessage(`🖥️ Führe EPLAN-Befehl aus:\n\`\`\`\n${cmdLine}\n\`\`\``);
            triggerEplanAction(args.action, args.project, args.params);
            toolResult = { success: true };
          } catch (err) {
            toolResult = { error: err.message };
          }
        }
      } else {
        toolResult = { error: `Unbekanntes Tool: ${tc.function.name}` };
      }

      // If the call failed validation, keep tools active so the AI can retry with corrected params.
      const needsRetry = !!(toolResult.error && tc.function.name === 'eplan_execute' && !toolResult.fatalError);
      const retryOptions = needsRetry
        ? { tools: EPLAN_TOOLS, tool_choice: 'auto' }
        : { responseFormat: { type: 'text' } };

      const firstAiMsg = { role: 'assistant', content: response.message.content || null, tool_calls: response.message.tool_calls };
      const firstToolMsg = { role: 'tool', tool_call_id: tc.id, content: JSON.stringify(toolResult) };

      response = await openaiService._rawChat(
        [...messages, firstAiMsg, firstToolMsg],
        retryOptions
      );

      // If the retry also produced a corrected tool call, execute it now
      if (needsRetry && response.message.tool_calls?.length) {
        const tc2 = response.message.tool_calls[0];
        let args2;
        try { args2 = JSON.parse(tc2.function.arguments); } catch { args2 = {}; }

        let toolResult2;
        if (tc2.function.name === 'eplan_execute' && args2.params?.TYPE) {
          try {
            const cmdLine2 = buildEplanCommand(args2.action, args2.project, args2.params);
            appendMessage(`🖥️ Führe EPLAN-Befehl aus:\n\`\`\`\n${cmdLine2}\n\`\`\``);
            triggerEplanAction(args2.action, args2.project, args2.params);
            toolResult2 = { success: true };
          } catch (err) {
            toolResult2 = { error: err.message };
          }
        } else {
          toolResult2 = { error: 'params.TYPE fehlt auch im zweiten Versuch.' };
        }

        const secondAiMsg = { role: 'assistant', content: response.message.content || null, tool_calls: response.message.tool_calls };
        const secondToolMsg = { role: 'tool', tool_call_id: tc2.id, content: JSON.stringify(toolResult2) };

        response = await openaiService._rawChat(
          [...messages, firstAiMsg, firstToolMsg, secondAiMsg, secondToolMsg],
          { responseFormat: { type: 'text' } }
        );
      }
    }

    hideThinkingIndicator();

    // Add assistant reply to history
    conversationHistory.push({ role: "assistant", content: response.message.content });

    if (response.quota) {
      updateQuotaDisplay(response.quota);
    }

    appendMessage(response.message.content);
  } catch (error) {
    hideThinkingIndicator();
    // Remove the user message we optimistically added
    conversationHistory.pop();
    handleApiError(error, currentModel, appendMessage);
  }
}

async function handleDataRequest(sheet, promptData, message, chatInput) {
  const { isPartsData, systemPrompt, userPrompt, originalValues, metadata } = promptData;
  const { startRow, startCol, rowCount, colCount } = metadata;
  const currentQuota = getCurrentQuota();
  const currentModel = getCurrentModel();
  
  const estimatedTokensCount = estimateTokens(systemPrompt + userPrompt);
  
  // Check if request would be too large
  if (currentQuota && currentQuota.limits && estimatedTokensCount > currentQuota.limits.max_tokens_per_request) {
    appendMessage(`❌ Deine Anfrage mit ca. ${estimatedTokensCount} Tokens überschreitet dein Limit von ${currentQuota.limits.max_tokens_per_request} Tokens pro Anfrage. Bitte wähle weniger Daten aus.`);
    return;
  }
  
  // Show user message and clear input
  appendMessage(message, true);
  chatInput.value = "";
  
  // Clear estimated usage display
  const estimatedDiv = document.getElementById("estimated-usage");
  if (estimatedDiv) estimatedDiv.textContent = "";
  
  showThinkingIndicator();
  
  try {
    // Build RAG context from both sources (Hybrid: SharePoint + Azure AI Search)
    let ragContext = null;
    if (useRAGContext) {
      const features = getRagFeatures();
      const hasCloud = features.includes('cloud');
      const hasUpload = features.includes('upload');
      const contextParts = [];

      if (!hasCloud && !hasUpload) {
        // No document features enabled — skip all document searches
      } else {
        // 1. SharePoint/OneDrive context (requires 'cloud' feature)
        if (hasCloud && isSharePointConfigured()) {
          try {
            const config = loadSharePointConfig();
            const spContext = await buildSharePointContext(message, config);
            if (spContext) {
              contextParts.push(`=== SharePoint/OneDrive Dokumente ===\n${spContext}`);
              console.log("SharePoint RAG context added");
            }
          } catch (ragError) {
            console.warn("SharePoint search failed, continuing:", ragError);
          }
        }
        
        // 2. Azure AI Search context (requires 'upload' feature)
        if (hasUpload) {
          try {
            const azureContext = await buildAzureSearchContext(message);
            if (azureContext) {
              contextParts.push(`=== Hochgeladene Dokumente ===\n${azureContext}`);
              console.log("Azure AI Search RAG context added");
            }
          } catch (ragError) {
            console.warn("Azure Search failed, continuing:", ragError);
          }
        }
        
        // Combine contexts
        if (contextParts.length > 0) {
          ragContext = contextParts.join('\n\n');
        }
      }
    }
    
    // Enhance system prompt with RAG context if available
    let enhancedSystemPrompt = systemPrompt;
    if (ragContext) {
      enhancedSystemPrompt = `${systemPrompt}

--- RELEVANTER KONTEXT AUS DOKUMENTEN ---
Die folgenden Informationen aus internen Dokumenten könnten für diese Anfrage relevant sein:

${ragContext}

--- ENDE KONTEXT ---
Nutze diese Informationen wenn sie für die Anfrage relevant sind. Wenn die Dokumente nicht relevant sind, ignoriere sie.`;
    }
    
    const responseFormat = createResponseFormat(isPartsData, rowCount, colCount);
    
    // Estimate output size: the model echoes back the data as JSON.
    // userPrompt contains the serialized data — its token count is a good proxy for output size.
    const estimatedOutputTokens = estimateTokens(userPrompt);

    console.log("SystemPrompt: ", enhancedSystemPrompt);
    console.log("UserPrompt: ", userPrompt);
    
    // Trim history to limit token usage
    if (conversationHistory.length > MAX_HISTORY_MESSAGES) {
      conversationHistory = conversationHistory.slice(-MAX_HISTORY_MESSAGES);
    }

    const response = await openaiService.chat([
      { role: "system", content: enhancedSystemPrompt },
      ...conversationHistory,
      // Send full userPrompt (with Excel data) as current message, but don't store it in history
      { role: "user", content: userPrompt }
    ], responseFormat, estimatedOutputTokens);

    // Store only the plain message in history (exclude Excel data to save tokens)
    conversationHistory.push({ role: "user", content: message });
    
    console.log("Response: ", response);

    hideThinkingIndicator();
    
    // Update quota display if available
    if (response.quota) {
      updateQuotaDisplay(response.quota);
    }
    
    // Parse and validate response
    const aiResponse = JSON.parse(response.message.content);
    
    if (!aiResponse.text) {
      throw new Error("Antwort hat nicht das erwartete Format (text erforderlich)");
    }
    
    if (isPartsData && (!aiResponse.data)) {
      throw new Error("Antwort hat nicht das erwartete Format (text und data erforderlich)");
    }
    
    // Write data to Excel if in Edit Mode and data is provided
    const shouldWriteData = isEditMode() && aiResponse.data && aiResponse.data.length > 0;
    
    if (shouldWriteData) {
      
      console.log("Response: ", aiResponse.data);  
    
      const expectedCols = isPartsData ? colCount + 1 : colCount;
      const expectedRows = isPartsData ? rowCount + 1 : rowCount;
      const adjustedData = validateAndAdjustData(aiResponse.data, expectedRows, expectedCols);
      
      // For parts data, strip the header row (index 0) and Artikelnummer column (col 0) before writing
      const dataToWrite = isPartsData ? adjustedData.slice(1).map(row => row.slice(1)) : adjustedData;
      
      const targetRange = sheet.getRangeByIndexes(startRow, startCol, rowCount, colCount);
      targetRange.values = dataToWrite;
      await sheet.context.sync();
      
      await highlightChangedCells(sheet, dataToWrite, originalValues, startRow, startCol);
    }
    
    // Add only the text reply to history (exclude data array to save tokens)
    conversationHistory.push({ role: "assistant", content: aiResponse.text });

    // Show assistant's response
    appendMessage(aiResponse.text);
    
  } catch (error) {
    hideThinkingIndicator();
    // Remove the user message we optimistically added
    conversationHistory.pop();
    
    if (error instanceof SyntaxError) {
      console.error("Error parsing AI response:", error);
      appendMessage(`❌ Fehler beim Verarbeiten der Antwort: Ungültiges JSON-Format`);
    } else {
      handleApiError(error, currentModel, appendMessage);
    }
  }
}